import { AxiosError } from 'axios';
import { api } from './apiClient';
import { QueryBuilderRequest, QueryBuilderPreviewResponse, QueryBuilderExportResponse, ListQueryBuilderExportsResponse } from '../components/Admin/QueryBuilder/types';
import { IepTimerMode } from '../types/accommodations';

/**
 * Get all users data for CSV download
 * @returns JSON response with users data that can be converted to CSV
 */
export const getAllUsersForDownload = async (): Promise<{success: boolean; users: any[]}> => {
  try {
    const response = await api.get('/admin/downloads/users');
    
    if (!response.data || !response.data.success) {
      throw new Error('Failed to get users data for download');
    }
    
    return response.data;
  } catch (error) {
    console.error('Error downloading users data:', error);
    throw error instanceof Error ? error : new Error('Failed to download users data');
  }
};

/**
 * Get all sessions within a date range for CSV download
 * @param startDate - Start date in YYYY-MM-DD format
 * @param endDate - End date in YYYY-MM-DD format
 * @returns JSON response with sessions data that can be converted to CSV
 */
export const getSessionsForDownload = async (
  startDate: string, 
  endDate: string
): Promise<{success: boolean; sessions: any[]}> => {
  try {
    const response = await api.get('/admin/downloads/sessions', {
      params: { startDate, endDate }
    });
    
    if (!response.data || !response.data.success) {
      throw new Error('Failed to get sessions data for download');
    }
    
    return response.data;
  } catch (error) {
    console.error('Error downloading sessions data:', error);
    throw error instanceof Error ? error : new Error('Failed to download sessions data');
  }
};


/**
 * Get user progress data across all tracks for CSV download
 * @param userId - Optional user ID to filter by
 * @returns JSON response with progress data that can be converted to CSV
 */
export const getUserProgressForDownload = async (
  userId?: string
): Promise<{success: boolean; progress: any[]}> => {
  try {
    const response = await api.get('/admin/downloads/user-progress', {
      params: userId ? { userId } : {}
    });
    
    if (!response.data || !response.data.success) {
      throw new Error('Failed to get user progress data for download');
    }
    
    return response.data;
  } catch (error) {
    console.error('Error downloading user progress data:', error);
    throw error instanceof Error ? error : new Error('Failed to download user progress data');
  }
};

/**
 * Create new users through roster import
 * @param userData - Array of user data objects to create
 * @returns JSON response with creation results
 */
export const createUsers = async (
  userData: any[]
): Promise<{
  success: boolean; 
  message: string;
  results?: {
    totalProcessed: number;
    created: number;
    alreadyExisting: number;
    failed: number;
    details: Array<{
      email: string;
      userId?: string;
      status: string;
      oneRosterStatus?: string;
      message?: string;
    }>;
  };
}> => {
  try {
    const response = await api.post('/admin/user-rostering/create', {
      users: userData
    });
    
    if (!response.data || !response.data.success) {
      throw new Error('Failed to create users');
    }
    
    return response.data;
  } catch (error) {
    console.error('Error creating users:', error);
    throw error instanceof Error ? error : new Error('Failed to create users');
  }
};

/**
 * Update multiple users' grades in a batch
 * @param updates - Array of objects with email and grade
 * @returns Response with success status and results
 */
export const updateUserGradesBatch = async (
  updates: { email: string; grade: number }[]
): Promise<{ 
  success: boolean; 
  message: string; 
  results?: Array<{
    email: string;
    success: boolean;
    message: string;
    userId?: string;
  }>;
}> => {
  try {
    const response = await api.put('/admin/users/grades/batch', { updates });
    
    return response.data;
  } catch (error) {
    console.error('Error batch updating user grades:', error);
    if (error instanceof AxiosError && error.response) {
      return error.response.data;
    }
    throw error instanceof Error ? error : new Error('Failed to batch update user grades');
  }
};

/**
 * Update multiple users' tracks in a batch
 * @param updates - Array of objects with email and track
 * @returns Response with success status and results
 */
export const updateUserTracksBatch = async (
  updates: { email: string; track: string }[]
): Promise<{
  success: boolean;
  message: string;
  results?: Array<{
    email: string;
    success: boolean;
    message: string;
    userId?: string;
  }>;
}> => {
  try {
    const response = await api.put('/admin/users/tracks/batch', { updates });

    return response.data;
  } catch (error) {
    console.error('Error batch updating user tracks:', error);
    if (error instanceof AxiosError && error.response) {
      return error.response.data;
    }
    throw error instanceof Error ? error : new Error('Failed to batch update user tracks');
  }
};

/**
 * Move a student to a different grade and/or track
 * Updates user profile in DynamoDB and syncs OneRoster enrollments if track changes
 * @param email - The student's email address
 * @param ageGrade - Optional new age grade
 * @param focusTrack - Optional new focus track
 * @returns Response with success status and details
 */
export const moveStudent = async (
  email: string,
  ageGrade?: number,
  focusTrack?: string
): Promise<{
  success: boolean;
  message: string;
  userId?: string;
  profileUpdated?: boolean;
  enrollmentResult?: {
    success: boolean;
    message: string;
    transferredFrom?: string;
    transferredTo?: string;
  };
}> => {
  try {
    const response = await api.post('/admin/users/move', {
      email,
      ageGrade,
      focusTrack
    });

    return response.data;
  } catch (error) {
    console.error('Error moving student:', error);
    if (error instanceof AxiosError && error.response) {
      return error.response.data;
    }
    throw error instanceof Error ? error : new Error('Failed to move student');
  }
};

export interface StudentAccommodationState {
  email: string;
  userId: string;
  iepAccommodationsEnabled: boolean;
  iepTimerMode: IepTimerMode;
}

const getAccommodationErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof AxiosError) {
    const message = error.response?.data?.message;
    if (typeof message === 'string' && message.trim()) return message;
  }
  return error instanceof Error ? error.message : fallback;
};

const normalizeAccommodationState = (student: Omit<StudentAccommodationState, 'iepTimerMode'> & {
  iepTimerMode?: IepTimerMode;
}): StudentAccommodationState => ({
  ...student,
  iepTimerMode: student.iepTimerMode ?? (student.iepAccommodationsEnabled ? '2x' : 'standard')
});

export const getStudentAccommodation = async (email: string): Promise<StudentAccommodationState> => {
  try {
    const response = await api.get('/admin/users/accommodations', { params: { email } });
    return normalizeAccommodationState(response.data.student);
  } catch (error) {
    throw new Error(getAccommodationErrorMessage(error, 'Failed to find student.'));
  }
};

export const setStudentAccommodation = async (
  email: string,
  iepAccommodationsEnabled: boolean,
  iepTimerMode: IepTimerMode
): Promise<StudentAccommodationState> => {
  try {
    const response = await api.put('/admin/users/accommodations', {
      email,
      iepAccommodationsEnabled,
      iepTimerMode
    });
    return normalizeAccommodationState(response.data.student);
  } catch (error) {
    throw new Error(getAccommodationErrorMessage(error, 'Failed to update accommodations.'));
  }
};

export const moveEarlyGradeStudent = async (
  email: string,
  ageGrade: -1 | 0,
  focusTrack: string
): Promise<{
  success: boolean;
  message: string;
  userId?: string;
  profileUpdated?: boolean;
  enrollmentResult?: {
    success: boolean;
    message: string;
    transferredFrom?: string;
    transferredTo?: string;
  };
}> => {
  try {
    const response = await api.post('/admin/users/move-early-grade', {
      email,
      ageGrade,
      focusTrack
    });
    return response.data;
  } catch (error) {
    console.error('Error moving early-grade student:', error);
    if (error instanceof AxiosError && error.response) {
      return error.response.data;
    }
    throw error instanceof Error ? error : new Error('Failed to move student');
  }
};

/**
 * Reset a user's progress for a specific track.
 * @param email - The email of the user to reset.
 * @param trackId - The track to reset progress for.
 * @returns Response with success status and message.
 */
export const resetUserProgress = async (
  email: string,
  trackId: string
): Promise<{ 
  success: boolean; 
  message: string; 
}> => {
  try {
    const response = await api.post('/admin/users/progress/reset', { email, trackId });
    
    return response.data;
  } catch (error) {
    console.error('Error resetting user progress:', error);
    if (error instanceof AxiosError && error.response) {
      return error.response.data;
    }
    throw error instanceof Error ? error : new Error('Failed to reset user progress');
  }
};



/**
 * User metrics interface for Caliper data
 */
export interface UserMetrics {
  userEmail: string;
  activeTime: number;
  wasteTime: number;
  xpTotal: number;
  masteredUnits: number;
  totalQuestions: number;
  correctQuestions: number;
  averageAccuracy: number;
  averageXpPerDay: number;
  trackId?: string; // Optional for track-specific data
}

/**
 * Caliper metrics response interface
 */
export interface CaliperMetricsResponse {
  users: UserMetrics[];
  dateRange: {
    startDate: string;
    endDate: string;
  };
  appName: string;
  totalUsers: number;
}

/**
 * Get Caliper metrics for AlphaNumbers app within a date range
 * @param startDate - Start date in YYYY-MM-DD format
 * @param endDate - End date in YYYY-MM-DD format
 * @returns JSON response with Caliper metrics data
 */
export const getCaliperMetrics = async (
  startDate: string,
  endDate: string
): Promise<CaliperMetricsResponse> => {
  try {
    const response = await api.get('/admin/caliper', {
      params: { startDate, endDate }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error fetching Caliper metrics:', error);
    if (error instanceof AxiosError && error.response) {
      throw new Error(error.response.data?.message || 'Failed to fetch Caliper metrics');
    }
    throw error instanceof Error ? error : new Error('Failed to fetch Caliper metrics');
  }
};

/**
 * Assessment result interface
 */
export interface AssessmentResult {
  userEmail: string;
  speedScore: number;
  date: string;
  trackId: string;
  ageGrade: number | null;
}

/**
 * Assessment results response interface
 */
export interface AssessmentResultsResponse {
  assessments: AssessmentResult[];
}

/**
 * Get assessment results within a date range
 * @param startDate - Start date in YYYY-MM-DD format
 * @param endDate - End date in YYYY-MM-DD format
 * @returns JSON response with assessment results data
 */
export const getAssessmentResults = async (
  startDate: string,
  endDate: string
): Promise<AssessmentResultsResponse> => {
  try {
    const response = await api.get('/admin/assessment-results', {
      params: { startDate, endDate }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error fetching assessment results:', error);
    if (error instanceof AxiosError && error.response) {
      throw new Error(error.response.data?.message || 'Failed to fetch assessment results');
    }
    throw error instanceof Error ? error : new Error('Failed to fetch assessment results');
  }
};

/**
 * Unroster a user by changing their password to 'AccountClosed'
 * @param email - The email of the user to unroster
 * @returns Response with success status and message
 */
export const unrosterUser = async (
  email: string
): Promise<{ 
  success: boolean; 
  message: string;
  user?: {
    userId: string;
    email: string;
    name: string;
  };
}> => {
  try {
    const response = await api.post('/admin/users/unroster', { email });
    
    return response.data;
  } catch (error) {
    console.error('Error unrostering user:', error);
    if (error instanceof AxiosError && error.response) {
      return error.response.data;
    }
    throw error instanceof Error ? error : new Error('Failed to unroster user');
  }
};

/**
 * Learning speed data interface
 */
export interface LearningSpeedUser {
  userEmail: string;
  ageGrade: number | null;
  knowledgeGrade: number | null;
  highestScore: number | null;
  scoreTarget: number | null;
  totalTimeHours: number;
  allocatedTimeHours: number;
  status: string;
  isSpendingEnoughTime?: boolean;
}

/**
 * Learning speed response interface
 */
export interface LearningSpeedResponse {
  users: LearningSpeedUser[];
  totalUsers: number;
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

/**
 * Get learning speed data (fixed date range from September 1, 2025 to present)
 * @returns JSON response with learning speed data
 */
export const getLearningSpeedData = async (): Promise<LearningSpeedResponse> => {
  try {
    const response = await api.get('/admin/learning-speed');

    return response.data;
  } catch (error) {
    console.error('Error fetching learning speed data:', error);
    if (error instanceof AxiosError && error.response) {
      throw new Error(error.response.data?.message || 'Failed to fetch learning speed data');
    }
    throw error instanceof Error ? error : new Error('Failed to fetch learning speed data');
  }
};

/**
 * Learning speed v2 data interface
 */
export interface LearningSpeedUser2 {
  userEmail: string;
  ageGrade: number | null;
  knowledgeGrade: number | null;
  totalTimeHours: number;
  activeTimeHours: number;
  xpTotal: number;
  allocatedTimeHours: number;
  allocatedXP: number;
  highestCQPM: number | null;
  targetCQPM: number | null;
  factsMastered: number;
  totalFacts: number;
  ls1: number | null;
  ls2: number | null;
  status: 'On-track' | 'Needs attention' | 'At risk' | 'Above age grade' | 'All grades mastered' | '-' | 'Error - Age Grade Missing';
  isSpendingEnoughTime: boolean;
  isSpendingEnoughActiveTime: boolean;
  isSpendingEnoughXP: boolean;
  highestTrackId: string | null;
}

/**
 * Learning speed v2 response interface
 */
export interface LearningSpeedResponse2 {
  users: LearningSpeedUser2[];
  totalUsers: number;
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

/**
 * Get learning speed v2 data (custom date range)
 * @param startDate - Start date in YYYY-MM-DD format
 * @param endDate - End date in YYYY-MM-DD format
 * @returns JSON response with learning speed v2 data
 */
export const getLearningSpeedV2Data = async (
  startDate: string,
  endDate: string
): Promise<LearningSpeedResponse2> => {
  try {
    const response = await api.get('/admin/learning-speed-v2', {
      params: { startDate, endDate }
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching learning speed v2 data:', error);
    if (error instanceof AxiosError && error.response) {
      throw new Error(error.response.data?.message || 'Failed to fetch learning speed v2 data');
    }
    throw error instanceof Error ? error : new Error('Failed to fetch learning speed v2 data');
  }
};

/**
 * Aggregate time spent user interface
 */
export interface AggregateTimeUser {
  userEmail: string;
  ageGrade: number | null;
  knowledgeGrade: number;
  focusTrack: string | null;
  totalTimeHours: number;
  activeTimeHours: number;
  xpTotal: number;
  allocatedTimeHours: number;
  allocatedXP: number;
  isSpendingEnoughTime: boolean;
  isSpendingEnoughActiveTime: boolean;
  isSpendingEnoughXP: boolean;
  meetsTimeMinimum: boolean;
  meetsActiveTimeMinimum: boolean;
  meetsXPMinimum: boolean;
}

/**
 * Aggregate time spent response interface
 */
export interface AggregateTimeSpentResponse {
  users: AggregateTimeUser[];
  totalUsers: number;
  dateRange: {
    startDate: string;
    endDate: string;
  };
  weekdaysCount: number;
}

/**
 * Get aggregate time spent data
 * @returns JSON response with aggregate time spent data
 */
export const getAggregateTimeSpentData = async (): Promise<AggregateTimeSpentResponse> => {
  try {
    const response = await api.get('/admin/aggregate-time-spent');

    return response.data;
  } catch (error) {
    console.error('Error fetching aggregate time spent data:', error);
    if (error instanceof AxiosError && error.response) {
      throw new Error(error.response.data?.message || 'Failed to fetch aggregate time spent data');
    }
    throw error instanceof Error ? error : new Error('Failed to fetch aggregate time spent data');
  }
};

/**
 * User Caliper event interface
 */
export interface UserCaliperEvent {
  eventTime: string;
  eventType: string;
  activityType: string | null;
  items: Array<{ type: string; value: number }>;
  trackId?: string;
}

/**
 * User Caliper events response interface
 */
export interface UserCaliperEventsResponse {
  events: UserCaliperEvent[];
  userEmail: string;
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

/**
 * Get individual Caliper events for a specific user
 * @param userEmail - The email of the user
 * @param startDate - Start date in YYYY-MM-DD format
 * @param endDate - End date in YYYY-MM-DD format
 * @returns JSON response with user Caliper events
 */
export const getUserCaliperEvents = async (
  userEmail: string,
  startDate: string,
  endDate: string
): Promise<UserCaliperEventsResponse> => {
  try {
    const response = await api.get('/admin/caliper/user', {
      params: { userEmail, startDate, endDate }
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching user Caliper events:', error);
    if (error instanceof AxiosError && error.response) {
      throw new Error(error.response.data?.message || 'Failed to fetch user Caliper events');
    }
    throw error instanceof Error ? error : new Error('Failed to fetch user Caliper events');
  }
};

/**
 * Mastery record interface (for "all grades mastered")
 */
export interface MasteryRecord {
  userEmail: string;
  ageGrade: number | null;
  masteryDate: string; // ISO date string (YYYY-MM-DD)
}

/**
 * Grade-specific mastery record interface (for individual grade masteries)
 */
export interface GradeMasteryRecord {
  userEmail: string;
  ageGrade: number | null;
  masteryDate: string; // ISO date string (YYYY-MM-DD)
  gradeLevel: number; // K=0, 1=1, 2=2, 3=3, 4=4
}

/**
 * Grade mastery response interface
 */
export interface GradeMasteryResponse {
  allGradesMastered: MasteryRecord[]; // Users who mastered all 4 L2 tracks
  anyGradeMastered: GradeMasteryRecord[]; // Individual grade mastery events
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

/**
 * Get grade mastery data (users who achieved various grade-level masteries)
 * @param startDate - Start date in YYYY-MM-DD format
 * @param endDate - End date in YYYY-MM-DD format
 * @returns JSON response with grade mastery data
 */
export const getGradeMasteryData = async (
  startDate: string,
  endDate: string
): Promise<GradeMasteryResponse> => {
  try {
    const response = await api.get('/admin/grade-mastery', {
      params: { startDate, endDate }
    });

    return response.data;
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      throw new Error(error.response.data?.message || 'Failed to fetch grade mastery data');
    }
    throw error instanceof Error ? error : new Error('Failed to fetch grade mastery data');
  }
};

/**
 * Student CQPM Analytics interfaces
 */
export interface AssessmentDataPoint {
  date: string;
  cqpm: number;
  accuracy: number;
}

export interface TrendlineData {
  slope: number;
  intercept: number;
  minCQPM: number;
  maxCQPM: number;
}

export interface TrackAnalytics {
  trackId: string;
  trackName: string;
  assessments: AssessmentDataPoint[];
  trendline: TrendlineData;
  daysElapsed: number;
  xpSpent: number;
  newFactsCount: number;
  learningRate: number;
  activeTimeSpent: number;
  wasteTimeSpent: number;
  totalTimeSpent: number;
}

export interface GradeAnalytics {
  grade: number;
  gradeName: string;
  tracks: TrackAnalytics[];
  gradeMetrics: {
    totalXpSpent: number;
    totalCqpmGain: number;
    totalNewFacts: number;
    gradeLearningRate: number;
    benchmarkRate: number;
    totalActiveTimeSpent: number;
    totalWasteTimeSpent: number;
    totalTimeSpent: number;
  };
}

export interface StudentCqpmAnalyticsResponse {
  studentEmail: string;
  gradeData: GradeAnalytics[];
}

/**
 * Get student CQPM analytics by email
 * @param email - Student email address
 * @returns JSON response with student CQPM analytics
 */
export const getStudentCqpmAnalytics = async (
  email: string
): Promise<StudentCqpmAnalyticsResponse> => {
  try {
    const response = await api.get('/admin/student-cqpm-analytics', {
      params: { email }
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching student CQPM analytics:', error);
    if (error instanceof AxiosError && error.response) {
      const messageParts = [
        error.response.data?.message,
        error.response.data?.details
      ].filter((value, index, array): value is string => Boolean(value) && array.indexOf(value) === index);

      throw new Error(
        messageParts.join(': ') ||
        'Failed to fetch student CQPM analytics'
      );
    }
    throw error instanceof Error ? error : new Error('Failed to fetch student CQPM analytics');
  }
};

/**
 * App Performance Dashboard interfaces
 */
export interface AppPerformanceMetricValues {
  median: number;
  mean: number;
  studentCount: number;
}

export interface AppPerformanceMetricSet {
  overall: AppPerformanceMetricValues;
  byGrade: Record<string, AppPerformanceMetricValues>;
}

export interface AppPerformanceDataPoint {
  date: string;
  cqpmPer100Xp: AppPerformanceMetricSet;
  activeDays: AppPerformanceMetricSet;
  xpPerActiveDay: AppPerformanceMetricSet;
  activeTimePerActiveDay: AppPerformanceMetricSet;
  totalTimePerActiveDay: AppPerformanceMetricSet;
}

export interface AppPerformanceResponse {
  dataPoints: AppPerformanceDataPoint[];
  isComputing: boolean;
}

/**
 * Get app performance data (CQPM per 100 XP over time)
 * @returns JSON response with daily data points
 */
export const getAppPerformanceData = async (): Promise<AppPerformanceResponse> => {
  try {
    const response = await api.get('/admin/app-performance-data');
    return response.data;
  } catch (error) {
    console.error('Error fetching app performance data:', error);
    if (error instanceof AxiosError && error.response) {
      throw new Error(error.response.data?.message || 'Failed to fetch app performance data');
    }
    throw error instanceof Error ? error : new Error('Failed to fetch app performance data');
  }
};

/**
 * Admin alert types
 */
export type AlertType = 'LOW_CQPM_PROGRESS' | 'CONSECUTIVE_FAILS';

/**
 * Admin alert interface
 */
export interface AdminAlert {
  alertType: AlertType;
  userId: string;
  userEmail: string;
  trackId: string;
  knowledgeGrade: number;
  triggeredDate: string;
  createdAt: string;
  metadata?: {
    slope?: number;
    dataPointsCount?: number;
    consecutiveFailCount?: number;
  };
}

/**
 * Admin alerts response interface
 */
export interface AdminAlertsResponse {
  alerts: AdminAlert[];
  dateRange: {
    startDate: string;
    endDate: string;
  };
  totalCount: number;
}

/**
 * Get admin alerts within a date range
 * @param startDate - Start date in YYYY-MM-DD format
 * @param endDate - End date in YYYY-MM-DD format
 * @param alertType - Optional alert type filter
 * @returns JSON response with alerts
 */
export const getAdminAlerts = async (
  startDate: string,
  endDate: string,
  alertType?: AlertType
): Promise<AdminAlertsResponse> => {
  try {
    const params: Record<string, string> = { startDate, endDate };
    if (alertType) {
      params.alertType = alertType;
    }

    const response = await api.get('/admin/alerts', { params });

    return response.data;
  } catch (error) {
    console.error('Error fetching admin alerts:', error);
    if (error instanceof AxiosError && error.response) {
      throw new Error(error.response.data?.message || 'Failed to fetch admin alerts');
    }
    throw error instanceof Error ? error : new Error('Failed to fetch admin alerts');
  }
};

/**
 * Preview a query builder spec — returns up to 10 rows synchronously.
 * @param spec - QueryBuilderRequest
 * @returns QueryBuilderPreviewResponse
 */
export const previewQueryBuilder = async (
  spec: QueryBuilderRequest
): Promise<QueryBuilderPreviewResponse> => {
  try {
    const response = await api.post('/admin/query-builder/preview', spec);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      throw new Error(error.response.data?.message || 'Failed to run query builder preview');
    }
    throw error instanceof Error ? error : new Error('Failed to run query builder preview');
  }
};

/**
 * Submit a full async export for the given query builder spec.
 * The backend enqueues the job and returns immediately; results arrive by email.
 * @param spec - QueryBuilderRequest
 * @returns QueryBuilderExportResponse
 */
export const runQueryBuilderExport = async (
  spec: QueryBuilderRequest
): Promise<QueryBuilderExportResponse> => {
  try {
    const response = await api.post('/admin/query-builder/export', spec);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      throw new Error(error.response.data?.message || 'Failed to start query builder export');
    }
    throw error instanceof Error ? error : new Error('Failed to start query builder export');
  }
};

/**
 * List the most recent query builder exports for the current admin (up to 10).
 * @returns ListQueryBuilderExportsResponse
 */
export const listQueryBuilderExports = async (): Promise<ListQueryBuilderExportsResponse> => {
  try {
    const response = await api.get('/admin/query-builder/exports');
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      throw new Error(error.response.data?.message || 'Failed to fetch recent exports');
    }
    throw error instanceof Error ? error : new Error('Failed to fetch recent exports');
  }
};
